import { router } from '@inertiajs/react'
import { useState } from 'react'
import { HiOutlineHandRaised } from 'react-icons/hi2'
import { FormUser } from './types'

type Props = {
  eventSlug: string
  users: FormUser[]
}

// Two-tap interaction (matches Figma): tap a row to select, tap Continue
// to commit. Server stores the selection in the per-event session slot.
export default function UserPicker({ eventSlug, users }: Props) {
  const [selected, setSelected] = useState<number | 'self_service' | null>(null)

  const handleContinue = () => {
    if (selected === null) return
    router.post(`/${eventSlug}/select_user`, {
      user_id: selected === 'self_service' ? 'self_service' : String(selected),
    })
  }

  return (
    <div className="min-h-screen w-full text-white" style={{ background: 'linear-gradient(180deg, #111d1e 50%, #004f51 100%)' }}>
      {/* Background sphere motif (CSS approximation of the Figma green glow) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-[-160px] top-[-200px] h-[700px] w-[700px] rounded-full"
        style={{
          background: 'radial-gradient(circle at 35% 35%, rgba(147,213,0,0.45), rgba(0,190,214,0.18) 40%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-[800px] flex-col">
        {/* Nav */}
        <header className="flex items-center justify-between bg-black/20 px-10 py-8">
          <div className="flex items-center gap-6">
            <div className="font-display text-2xl font-medium tracking-tight">CE</div>
            <p className="font-display text-2xl font-medium">Exhibition Intelligence MVP</p>
          </div>
          <a
            href="/login"
            className="rounded-md bg-white/5 px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-[inset_0_0_2px_0_rgba(255,255,255,0.6)] transition hover:bg-white/10"
          >
            Staff Portal
          </a>
        </header>

        <main className="flex flex-1 flex-col gap-5 px-12 py-20">
          <h1 className="font-display text-5xl font-bold leading-[1.15] text-white">Select User</h1>
          <p className="font-body text-lg text-white/90">Choose your profile to start capturing lead details.</p>

          <div className="mt-6 flex flex-col gap-3">
            {users.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => setSelected(u.id)}
                className={`flex items-center gap-4 rounded-md px-5 py-4 text-left transition ${
                  selected === u.id
                    ? 'bg-white/15 shadow-[inset_0_0_0_2px_rgba(0,190,214,0.9)]'
                    : 'bg-white/5 shadow-[inset_0_0_2px_0_rgba(255,255,255,0.6)] hover:bg-white/10'
                }`}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white text-base font-semibold text-[#111d1e]">
                  {u.initials}
                </div>
                <div className="flex flex-col">
                  <span className="font-display text-base font-semibold uppercase tracking-wide text-white">{u.name}</span>
                  <span className="font-body text-base text-white/60">{u.role === 'manager' ? 'Manager' : 'Staff'}</span>
                </div>
              </button>
            ))}

            <button
              type="button"
              onClick={() => setSelected('self_service')}
              className={`flex items-center gap-4 rounded-md px-5 py-4 text-left transition ${
                selected === 'self_service'
                  ? 'bg-white/15 shadow-[inset_0_0_0_2px_rgba(0,190,214,0.9)]'
                  : 'bg-white/5 shadow-[inset_0_0_2px_0_rgba(255,255,255,0.6)] hover:bg-white/10'
              }`}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white/20 text-white">
                <HiOutlineHandRaised className="h-6 w-6" />
              </div>
              <div className="flex flex-col">
                <span className="font-display text-base font-semibold uppercase tracking-wide text-white">Self Service</span>
                <span className="font-body text-base text-white/60">Visitor fills it in themselves</span>
              </div>
            </button>
          </div>

          <div className="mt-12 flex justify-end">
            <button
              type="button"
              disabled={selected === null}
              onClick={handleContinue}
              className="flex items-center gap-2 rounded-md bg-[#111d1e] px-8 py-4 font-display text-xl font-semibold tracking-tight text-white transition disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:bg-black"
            >
              Continue
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </main>
      </div>
    </div>
  )
}
